import type { ConnectionRequirement } from './integrations.ts';

export const TOOL_ACTION_KEYS = [
  'gmail.create_draft',
  'sales.sync_people_to_crm',
  'sales.apply_crm_outcome',
  'domino.apply_contextual_action',
] as const;

export type ToolActionKey = typeof TOOL_ACTION_KEYS[number];

export const isToolActionKey = (value: string): value is ToolActionKey => (
  TOOL_ACTION_KEYS.some((key) => key === value)
);

export const CRM_MUTATION_EFFECTS = [
  'upsert_company',
  'upsert_contacts',
  'associate_contacts_company',
  'create_note',
] as const;

export type CrmMutationEffect = typeof CRM_MUTATION_EFFECTS[number];

export const CONTEXTUAL_ACTION_CAPABILITY_KEYS = [
  'crm.company.upsert',
  'crm.contacts.upsert',
  'crm.contacts.associate_company',
  'crm.note.create',
] as const;

export type ContextualActionCapabilityKey = typeof CONTEXTUAL_ACTION_CAPABILITY_KEYS[number];

export interface ContextualActionEffect {
  capabilityKey: ContextualActionCapabilityKey;
  instruction?: string | null;
}

/** One row the user picked in the run's stored result (e.g. « Ajouter au CRM »
 * on a list). The server re-reads it; the client only names it. */
export interface ContextualActionResultRowTarget {
  kind: 'result_row';
  rowId: string;
  /** First cell of the row as displayed; must match the stored row. */
  label: string;
}

export interface ContextualActionSpec {
  version: 1;
  kind: 'contextual_action';
  source: 'selected_run';
  goal: string;
  effects: ContextualActionEffect[];
  target?: ContextualActionResultRowTarget | null;
}

export interface CrmMutationOutcomeSpec {
  version: 1;
  kind: 'crm_mutation';
  source: 'selected_run';
  effects: CrmMutationEffect[];
  noteInstruction?: string | null;
}

export type ToolActionOutcomeSpec = ContextualActionSpec | CrmMutationOutcomeSpec;

export interface ToolActionRequest {
  actionKey: ToolActionKey;
  sourceRunId: string;
  sourceActionId?: string | null;
  sourceActionIndex?: number | null;
  sourceActionLabel?: string | null;
  actionSpec?: ContextualActionSpec | null;
  /** @deprecated Compatibility for persisted CRM action cards. */
  outcomeSpec?: ToolActionOutcomeSpec | null;
  confirmationId?: string | null;
  confirmedCandidateIds?: string[] | null;
  confirmationEdits?: ToolActionConfirmationEdit[] | null;
}

export type ToolActionStatus =
  | 'needs_confirmation'
  | 'completed'
  | 'partially_completed'
  | 'already_done'
  | 'needs_connection'
  | 'missing_info'
  | 'failed';

export type ToolActionPreviewCandidateState =
  | 'create'
  | 'update'
  | 'unchanged'
  | 'ambiguous'
  | 'blocked';

export interface ToolActionPreviewCandidate {
  id: string;
  fullName: string;
  entityType?: 'company' | 'contact' | 'note' | 'task' | 'artifact' | 'action';
  jobTitle?: string | null;
  company?: string | null;
  email?: string | null;
  state: ToolActionPreviewCandidateState;
  stateLabel: string;
  detail?: string | null;
  contentPreview?: string | null;
  editableFields?: ToolActionEditableField[];
  selectedByDefault: boolean;
  required?: boolean;
}

export type ToolActionEditableField = 'contentPreview';

export interface ToolActionConfirmationEdit {
  candidateId: string;
  contentPreview?: string | null;
}

export interface ToolActionPreview {
  title: string;
  summary: string;
  companyStatus?: string | null;
  candidates: ToolActionPreviewCandidate[];
}

export interface ToolActionResponse {
  status: ToolActionStatus;
  actionKey: ToolActionKey;
  label: string;
  message: string;
  confirmationId?: string | null;
  preview?: ToolActionPreview | null;
  connectionRequirements?: ConnectionRequirement[];
  externalUrl?: string | null;
  providerRef?: {
    provider: 'gmail' | 'hubspot';
    draftId?: string | null;
    messageId?: string | null;
    threadId?: string | null;
    companyId?: string | null;
    contactIds?: string[] | null;
    noteId?: string | null;
  } | null;
}
